import { InfoTooltip } from '@/components/InfoTooltip'
import { NumericField } from '@/components/NumericField'
import { OptionalSettingSelect } from '@/components/OptionalSettingSelect'
import { numberField, parseOptionalNumber } from '@/lib/numberField'
import { ON_EXHAUSTED_LABELS, RETRY_TRIGGER_LABELS } from '@/lib/pageHintRetry'
import { rangeError } from '@/lib/settingRanges'
import type { GlobalPageHintRetry, PageHintRetrySettings } from '@/types/bundle'

export interface PageHintRetryTabProps {
  retry: GlobalPageHintRetry
  onChange: (retry: GlobalPageHintRetry) => void
}

// Shown when a setting is left blank: the engine then uses its own built-in
// value (backend PRD 39) - not repeated here, so it can never drift.
const BUILT_IN = 'Built-in default'

// Platform-wide page-hint retry settings (global_defaults.page_hint_retry,
// backend PRD 39). They only matter for a document type whose every field
// has a page hint; a document type can override the first four on its own
// General tab.
export function PageHintRetryTab({ retry, onChange }: PageHintRetryTabProps) {
  function update(patch: Partial<GlobalPageHintRetry>) {
    onChange({ ...retry, ...patch })
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Page hint retry
          <InfoTooltip text="Only used for document types where every field has a page hint (Document Types > Fields). The model is first shown just the hinted pages; these settings decide how it looks further when a field isn't found there." />
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <NumericField
            id="phr-max-widen-radius"
            label="Max widen radius (pages)"
            tooltip="How many extra rounds the platform tries, each looking one more page either side of a missing field's hint. 0 turns widening off."
            min={0}
            placeholder={BUILT_IN}
            value={numberField(retry.max_widen_radius_default)}
            error={rangeError('max_widen_radius_default', retry.max_widen_radius_default)}
            onChange={(raw) => update({ max_widen_radius_default: parseOptionalNumber(raw) })}
          />
          <NumericField
            id="phr-whole-document-threshold"
            label="Whole-document threshold (%)"
            tooltip="When the pages to send would cover at least this much of the document, the whole document is sent instead."
            min={0}
            max={100}
            placeholder={BUILT_IN}
            value={numberField(retry.whole_document_threshold_percent_default)}
            error={rangeError(
              'whole_document_threshold_percent_default',
              retry.whole_document_threshold_percent_default,
            )}
            onChange={(raw) =>
              update({ whole_document_threshold_percent_default: parseOptionalNumber(raw) })
            }
          />
          <OptionalSettingSelect
            label="Retry trigger"
            tooltip="Which missing fields start a retry. Critical is set per field on Document Types > Fields."
            value={retry.retry_trigger_default}
            labels={RETRY_TRIGGER_LABELS}
            unsetLabel={BUILT_IN}
            onChange={(value) =>
              update({
                retry_trigger_default: value as PageHintRetrySettings['retry_trigger_default'],
              })
            }
          />
          <OptionalSettingSelect
            label="When retries run out"
            tooltip="What happens if a field is still missing after the last widening round."
            value={retry.on_exhausted_default}
            labels={ON_EXHAUSTED_LABELS}
            unsetLabel={BUILT_IN}
            onChange={(value) =>
              update({
                on_exhausted_default: value as PageHintRetrySettings['on_exhausted_default'],
              })
            }
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Request-wide limits
          <InfoTooltip text="Apply to a whole request, across every document in it. Not overridable per document type." />
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <NumericField
            id="phr-max-extra-calls"
            label="Max extra calls per request"
            tooltip="The most extra model calls retries may make in one request, across all its documents. 0 turns retries off."
            min={0}
            placeholder={BUILT_IN}
            value={numberField(retry.max_extra_bedrock_calls_per_request)}
            error={rangeError(
              'max_extra_bedrock_calls_per_request',
              retry.max_extra_bedrock_calls_per_request,
            )}
            onChange={(raw) =>
              update({ max_extra_bedrock_calls_per_request: parseOptionalNumber(raw) })
            }
          />
          <NumericField
            id="phr-time-reserve"
            label="Time reserve (seconds)"
            tooltip="Seconds of the 28-second request time that retries may never use. A retry is skipped when it can't fit before this reserve."
            min={0}
            max={28}
            step="0.5"
            placeholder={BUILT_IN}
            value={numberField(retry.retry_time_reserve_seconds)}
            error={rangeError('retry_time_reserve_seconds', retry.retry_time_reserve_seconds)}
            onChange={(raw) => update({ retry_time_reserve_seconds: parseOptionalNumber(raw) })}
          />
        </div>
      </section>
    </div>
  )
}
